import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Volume2, RotateCcw, Play, Square, Check } from "lucide-react";
import { toast } from "sonner";
import {
  listVoices,
  getVoicePreference,
  setVoicePreference,
  speakWithVoice,
  stopSpeaking,
  enableAnnouncerSound,
} from "@/lib/announcer";

// Voices differ mainly in how they handle real patient names — Filipino names in
// particular — so audition with an actual name from the queue when we have one.
const sampleFor = (name) =>
  `Patient ${name || "Juan Dela Cruz"}, please proceed to the clinic.`;

// Which voices exist is a property of the machine, not the account — a clinic PC
// and the TV box can expose completely different sets. So this is a per-device
// setting stored locally, not part of the user's profile.
export default function CallOutVoicePicker({ sampleName = "", bare = false }) {
  const SAMPLE = sampleFor(sampleName);
  const [voices, setVoices] = useState([]);
  const [selected, setSelected] = useState(getVoicePreference());
  const [loading, setLoading] = useState(true);
  const [playingUri, setPlayingUri] = useState("");
  const [touring, setTouring] = useState(false);

  // Set when the user hits Stop, so an in-flight tour loop bails out instead of
  // continuing through the remaining voices.
  const stoppedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    enableAnnouncerSound();
    listVoices()
      .then((v) => { if (!cancelled) { setVoices(v); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; stopSpeaking(); };
  }, []);

  const choose = (uri) => {
    setSelected(uri);
    setVoicePreference(uri);
    toast.success(uri ? "Call-out voice saved for this device" : "Back to automatic");
  };

  const stop = () => {
    stoppedRef.current = true;
    stopSpeaking();
    setTouring(false);
    setPlayingUri("");
  };

  const playOne = async (v) => {
    stoppedRef.current = false;
    setPlayingUri(v.voiceURI);
    await speakWithVoice(SAMPLE, v.voiceURI);
    setPlayingUri("");
  };

  // Walk every voice, announcing its number first so the spoken audio and the
  // highlighted row stay identifiable even if you're not watching the screen.
  const playAll = async () => {
    stoppedRef.current = false;
    setTouring(true);
    for (let i = 0; i < voices.length; i++) {
      if (stoppedRef.current) break;
      const v = voices[i];
      setPlayingUri(v.voiceURI);
      await speakWithVoice(`Voice ${i + 1}. ${SAMPLE}`, v.voiceURI);
      if (stoppedRef.current) break;
      await new Promise((r) => setTimeout(r, 500));
    }
    setTouring(false);
    setPlayingUri("");
  };

  const Wrapper = bare ? "div" : Card;
  const wrapperProps = bare ? {} : { className: "p-5 border-slate-200" };

  return (
    <Wrapper {...wrapperProps} data-testid="callout-voice-picker">
      {bare ? null : (
        <h2 className="font-semibold text-slate-900 mb-1" style={{ fontFamily: "Work Sans" }}>
          Call-out Voice
        </h2>
      )}
      <p className="text-xs text-slate-500 mb-3">
        The voice used to announce patients on <b>this device</b>. Play through them
        and pick whichever reads the names most clearly.
        {sampleName ? <> Auditioning with <b>{sampleName}</b>.</> : null}
      </p>

      {loading ? (
        <div className="text-sm text-slate-500">Loading voices…</div>
      ) : voices.length === 0 ? (
        <div className="text-sm text-slate-500">
          This browser exposes no speech voices.
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2 flex-wrap">
            {touring ? (
              <Button onClick={stop} size="sm" variant="destructive" data-testid="btn-stop-voices">
                <Square size={14} className="mr-1" /> Stop
              </Button>
            ) : (
              <Button onClick={playAll} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="btn-play-all-voices">
                <Play size={14} className="mr-1" /> Play all {voices.length} voices
              </Button>
            )}
            {selected ? (
              <Button onClick={() => choose("")} variant="outline" size="sm" data-testid="btn-reset-voice">
                <RotateCcw size={14} className="mr-1" /> Back to automatic
              </Button>
            ) : null}
          </div>

          <div className="border border-slate-200 rounded-md divide-y divide-slate-100 max-h-80 overflow-auto">
            {voices.map((v, i) => {
              const isSelected = selected === v.voiceURI;
              const isPlaying = playingUri === v.voiceURI;
              return (
                <div
                  key={v.voiceURI}
                  className={
                    "flex items-center gap-2 px-3 py-2 " +
                    (isPlaying ? "bg-blue-50" : isSelected ? "bg-emerald-50" : "bg-white")
                  }
                  data-testid={`voice-row-${i}`}
                >
                  <span className="text-[11px] font-bold text-slate-400 w-6 shrink-0">{i + 1}</span>

                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-slate-900 truncate">
                      {v.name}
                      {v.likelyFemale ? (
                        <span className="ml-2 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-pink-50 text-pink-700">
                          female
                        </span>
                      ) : null}
                    </div>
                    <div className="text-[10px] text-slate-400">{v.lang}</div>
                  </div>

                  <button
                    type="button"
                    onClick={() => playOne(v)}
                    disabled={touring}
                    className="text-slate-500 hover:text-blue-700 disabled:opacity-40 shrink-0 p-1"
                    title="Hear this voice"
                    data-testid={`btn-play-voice-${i}`}
                  >
                    <Volume2 size={16} />
                  </button>

                  <Button
                    onClick={() => choose(v.voiceURI)}
                    size="sm"
                    variant={isSelected ? "default" : "outline"}
                    className={isSelected ? "bg-emerald-600 hover:bg-emerald-700" : ""}
                    data-testid={`btn-use-voice-${i}`}
                  >
                    {isSelected ? <><Check size={13} className="mr-1" /> In use</> : "Use this"}
                  </Button>
                </div>
              );
            })}
          </div>

          <div className="text-xs text-slate-400">
            {selected
              ? "This device will use the voice marked “In use”."
              : "Automatic: prefers a male voice and skips the ones tagged female."}
          </div>
        </div>
      )}
    </Wrapper>
  );
}
